/**
 * Lightweight Slideshow (No heavy libraries)
 */
class SlideshowLight extends HTMLElement {
    constructor() {
        super();
        this.currentSlide = 0;
        this.autoplayTimer = null;
    }

    connectedCallback() {
        this.init();
    }

    init() {
        // Elements
        this.track = this.querySelector('.slideshow-light__track');
        this.slides = Array.from(this.querySelectorAll('.slideshow-light__slide'));
        this.dots = this.querySelectorAll('.flickity-page-dots .dot');
        this.prevBtn = this.querySelector('.flickity-prev');
        this.nextBtn = this.querySelector('.flickity-next');
        
        // Settings
        this.autoplayEnabled = this.dataset.autoplay === 'true';
        this.autoplayInterval = parseInt(this.dataset.autoplayInterval) || 5000;
        this.transitionType = this.dataset.transition || 'fade';
        
        // External Tabs (if present)
        this.tabs = document.querySelectorAll('.slideshow-tab');

        // Events
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());
        
        this.dots.forEach((dot, index) => {
            dot.addEventListener('click', () => this.goTo(index));
        });

        if (this.tabs.length) {
            this.tabs.forEach((tab, index) => {
                tab.addEventListener('click', () => {
                    this.goTo(index);
                    // Update active tab visually
                    this.tabs.forEach(t => t.classList.remove('active'));
                    tab.classList.add('active');
                });
            });
        }

        // Swipe Support (Basic Touch)
        this.touchStartX = 0;
        this.touchEndX = 0;
        this.addEventListener('touchstart', e => this.touchStartX = e.changedTouches[0].screenX, {passive: true});
        this.addEventListener('touchend', e => {
            this.touchEndX = e.changedTouches[0].screenX;
            this.handleSwipe();
        }, {passive: true});

        // Pause autoplay on hover
        if (this.autoplayEnabled) {
            this.addEventListener('mouseenter', () => this.stopAutoplay());
            this.addEventListener('mouseleave', () => this.startAutoplay());
            this.startAutoplay();
        }

        // Initial State
        this.updateSlide(0);
        
        // Handle resize for slide transition
        window.addEventListener('resize', () => {
            if (this.transitionType === 'slide') {
                this.updateTransform();
            }
        });
    }

    handleSwipe() {
        if (this.touchEndX < this.touchStartX - 50) this.next();
        if (this.touchEndX > this.touchStartX + 50) this.prev();
    }

    startAutoplay() {
        if (!this.autoplayEnabled) return;
        this.stopAutoplay();
        this.autoplayTimer = setInterval(() => this.next(), this.autoplayInterval);
    }

    stopAutoplay() {
        if (this.autoplayTimer) clearInterval(this.autoplayTimer);
    }

    next() {
        let index = this.currentSlide + 1;
        if (index >= this.slides.length) index = 0;
        this.goTo(index);
    }

    prev() {
        let index = this.currentSlide - 1;
        if (index < 0) index = this.slides.length - 1;
        this.goTo(index);
    }

    goTo(index) {
        this.currentSlide = index;
        this.updateSlide(index);
        
        // Update external tabs if they exist
        if (this.tabs.length) {
            this.tabs.forEach(t => t.classList.remove('active'));
            if (this.tabs[index]) this.tabs[index].classList.add('active');
        }
    }

    updateSlide(index) {
        // Update Classes
        this.slides.forEach((slide, i) => {
            if (i === index) {
                slide.classList.add('active', 'is-selected');
            } else {
                slide.classList.remove('active', 'is-selected');
            }
        });

        // Update Dots
        this.dots.forEach((dot, i) => {
            if (i === index) {
                dot.classList.add('is-selected');
            } else {
                dot.classList.remove('is-selected');
            }
        });

        // Handle Transition
        if (this.transitionType === 'slide') {
            this.updateTransform();
        }

        // Handle Video (Play active, pause others)
        this.slides.forEach((slide, i) => {
            const videoContainer = slide.querySelector('.slideshow__slide-video-bg');
            if (!videoContainer) return;
            
            if (i === index) {
                this.playVideo(videoContainer);
            } else {
                this.pauseVideo(videoContainer);
            }
        });
        
        // Update text color for nav controls based on current slide setting
        const activeSlide = this.slides[index];
        const colorText = getComputedStyle(activeSlide).getPropertyValue('--color-body');
        const navWrapper = this.querySelector('.flickity-custom-navigation-wrapper');
        if (navWrapper) {
            navWrapper.style.setProperty('--color-body', colorText);
        }
    }

    updateTransform() {
        const width = this.offsetWidth;
        this.track.style.transform = `translateX(-${this.currentSlide * 100}%)`;
    }

    playVideo(container) {
        const video = container.querySelector('video');
        if (video) {
            video.play();
        } else if (container.dataset.provider === 'youtube') {
             container.querySelector('iframe')?.contentWindow.postMessage(JSON.stringify({
                event: "command",
                func: "playVideo",
                args: ""
            }), "*");
        } else if (container.dataset.provider === 'vimeo') {
             container.querySelector('iframe')?.contentWindow.postMessage(JSON.stringify({
                method: "play"
            }), "*");
        }
    }

    pauseVideo(container) {
        const video = container.querySelector('video');
        if (video) {
            video.pause();
        } else if (container.dataset.provider === 'youtube') {
            container.querySelector('iframe')?.contentWindow.postMessage(JSON.stringify({
                event: "command",
                func: "pauseVideo",
                args: ""
            }), "*");
        } else if (container.dataset.provider === 'vimeo') {
            container.querySelector('iframe')?.contentWindow.postMessage(JSON.stringify({
                method: "pause"
            }), "*");
        }
    }
}

customElements.define('slideshow-light', SlideshowLight);

